import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Person } from 'src/app/model/Person';
import { UserGroup } from 'src/app/model/UserGroup';
import { GroupService } from 'src/app/services/group/group.service';
import { ToastrService } from 'ngx-toastr';
import { PeopleService } from 'src/app/services/people/people.service';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { UserService } from 'src/app/services/user/user.service';


export interface UserManagementDialogData {
    person: Person;
}

/**
 * Dialog component for managing user details
 * Allows editing user group, title, and admin status
 */
@Component({
    selector: 'app-user-management-dialog',
    templateUrl: './user-management-dialog.component.html',
    styleUrls: ['./user-management-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserManagementDialogComponent implements OnInit {
    /** Available user groups for selection */
    public availableGroups: UserGroup[] = [];

    /** Edited person object properties */
    public edited!: Person;

    /** Original person object properties to track changes */
    private _original!: Person;

    /** Status on if an action button was clicked */
    public doingAction: boolean = false;

    /** Flag to indicate if the current user is the one being edited */
    public isSelf: boolean;

    /**
     * Default constructor to initialize the dialog component
     * 
     * @param data 
     * @param dialogRef 
     * @param _groupService 
     * @param _toastr 
     */
    constructor(
        @Inject(MAT_DIALOG_DATA) public data: UserManagementDialogData,
        public dialogRef: MatDialogRef<UserManagementDialogComponent>,
        private _groupService: GroupService,
        private _peopleService: PeopleService,
        private _toastr: ToastrService,
        private _dialogService: DialogService,
        private _userService: UserService,
    ) {
        // Set is self flag
        const currentUserId = this._userService.user?.id;
        this.isSelf = currentUserId ? currentUserId == Number.parseInt(this.data.person.id) : false;
    }

    /**
     * Initialize the component when the dialog is opened
     */
    ngOnInit(): void {
        // Initialize edited person with current values
        this.edited = this.data.person.copyOf();

        // Store original values for change detection
        this._original = this.data.person.copyOf();

        // Load available groups on initialization
        this.availableGroups = this._groupService.getEntities() || [];
    }

    /**
     * Load available groups for the dropdown
     */
    private _loadAvailableGroups(): void {
        this._groupService.changes$.subscribe(groups => {
            if (groups) {
                this.availableGroups = groups;
            }
        });
    }

    /**
     * Check if any changes have been made to the user
     */
    public hasUnsavedChanges(): boolean {
        return !this.edited.equals(this._original);
    }

    /**
     * Handle group change from dropdown
     */
    public onGroupChange(groupName: string): void {
        this.edited.group = groupName;
    }

    /**
     * Save the user changes
     */
    public onSave(): void {
        if (this.edited.group.toLowerCase() !== 'admin') {
            // If the user is not an admin, save the changes
            this._saveUserChanges();
            return;
        }

        // Otherwise, open confirmation dialog to confirm making admin
        const ref = this._dialogService.openConfirmationDialog({
            title: `Make ${this.edited.fullName} an admin?`,
            message: `Are you sure you want to make ${this.edited.firstName} an admin?`,
            actions: {
                align: 'center',
            },
            confirm: {
                text: 'Yes',
                color: 'primary',
            },
            cancel: {
                text: 'Cancel',
                color: '',
            },
        });

        // Subscribe to the confirmation dialog result
        ref.subscribe((result: boolean) => {
            // If user confirmed, proceed with group change
            if (result) this._saveUserChanges();
        });
    }

    /**
     * Helper method to save user changes
     */
    private _saveUserChanges(): void {
        console.log("[USER-MANAGEMENT-DIALOG]: Saving user changes", this.edited);
        this.doingAction = true;

        // Check if there are unsaved changes before proceeding
        if (!this.hasUnsavedChanges()) {
            console.log("[USER-MANAGEMENT-DIALOG]: No changes to save");
            return;
        }

        // Find group ID to edit to
        const groupId = this.availableGroups.find(g => g.name === this.edited.group)?.id;
        if (!groupId) {
            console.error("[USER-MANAGEMENT-DIALOG]: Selected group not found");
            this._toastr.error('Selected group not found', 'Error');
            return;
        }

        // Apply the selected group to the user
        this._groupService.applyGroupToUser(this.edited.id, groupId).subscribe({
            // On success, close the dialog and notify user
            next: () => {
                console.log("[USER-MANAGEMENT-DIALOG]: Successfully applied group to user");

                this.doingAction = false;
                this._toastr.success(`Group ${this.edited.group} applied to user!`);
                this.dialogRef.close({ action: 'save', data: this.edited });
            },
            // On error, log the error and notify user
            error: (error) => {
                console.error("[USER-MANAGEMENT-DIALOG]: Failed to apply group to user", error);

                this.doingAction = false;
                this._toastr.error('Failed to apply group to user');
            }
        });
    }

    /**
     * Deactivate the selected user
     */
    public onDeactivate(): void {
        // Open confirmation dialog to confirm deactivation
        const ref = this._dialogService.openConfirmationDialog({
            title: `Deactivate ${this.edited.fullName} from your account`,
            message: formatDeactivateDialogContent(this.edited),
            actions: {
                align: 'center',
            },
            confirm: {
                text: 'Deactivate',
                color: 'warn',
            },
            cancel: {
                text: 'Cancel',
                color: '',
            },
        });

        // Subscribe to the confirmation dialog result
        ref.subscribe((result: boolean) => {
            // If user confirmed, proceed with deactivation
            if (result) this._deactivateUser();
        });
    }

    /**
     * Reactivate the selected user
     */
    public onActivate(): void {
        // Open confirmation dialog to confirm reactivation
        const ref = this._dialogService.openConfirmationDialog({
            title: `Reactivate ${this.edited.fullName}`,
            message: formatReactivateDialogContent(this.edited),
            actions: {
                align: 'center',
            },
            confirm: {
                text: 'Reactivate',
                color: 'primary',
            },
            cancel: {
                text: 'Cancel',
                color: '',
            },
        });

        // Subscribe to the confirmation dialog result
        ref.subscribe((result: boolean) => {
            // If user confirmed, proceed with reactivation
            if (result) this._reactivateUser();
        });
    }

    /**
     * Helper method to handle user deactivation
     */
    private _deactivateUser(): void {
        console.log("[USER-MANAGEMENT-DIALOG]: Deactivating user", this.data.person);
        this.doingAction = true;

        // Call the service to deactivate the user
        try {
            this._peopleService.deactivatePerson(this.data.person.id).subscribe({
                next: () => {
                    console.log("[USER-MANAGEMENT-DIALOG]: Successfully deactivated user");

                    this.doingAction = false;
                    this._toastr.success('User deactivated successfully!');
                    this.dialogRef.close({ action: 'deactivate', data: this.data.person });
                },
                error: (error) => {
                    console.error("[USER-MANAGEMENT-DIALOG]: Failed to deactivate user", error);

                    this.doingAction = false;
                    this._toastr.error('Failed to deactivate user');
                }
            });
        } catch (error) {
            this._toastr.error('An unexpected error occurred while deactivating the user');
        }
    }

    /**
     * Helper method to handle user reactivation
     */
    private _reactivateUser(): void {
        console.log("[USER-MANAGEMENT-DIALOG]: Reactivating user", this.data.person);
        this.doingAction = true;

        // Call the service to deactivate the user
        try {
            this._peopleService.reactivatePerson(this.data.person.id).subscribe({
                next: () => {
                    console.log("[USER-MANAGEMENT-DIALOG]: Successfully reactivated user");

                    this.doingAction = false;
                    this._toastr.success('User reactivated successfully!');
                    this.dialogRef.close({ action: 'reactivate', data: this.data.person });
                },
                error: (error) => {
                    console.error("[USER-MANAGEMENT-DIALOG]: Failed to reactivate user", error);

                    this.doingAction = false;
                    this._toastr.error('Failed to reactivate user');
                }
            });
        } catch (error) {
            this._toastr.error('An unexpected error occurred while reactivating the user');
        }
    }

    /**
     * Close the dialog without saving
     */
    public cancel(): void {
        this.dialogRef.close();
    }
}

const formatDeactivateDialogContent = (person: Person): string => {
return `Are you sure you want to deactivate ${person.firstName} from your Transend account? ${person.fullName} will no longer have access to Transend or any functionality within the platform. This user's activity history will be maintained.
<br><br>
Don't worry, you can reactivate this user again in the future.`;
}

const formatReactivateDialogContent = (person: Person): string => {
    return `Are you sure you want to reactivate ${person.firstName}'s account? ${person.fullName} will regain access to Transend and all associated functionalities. This user will be able to log in and use the platform as before.`;
}